import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import FileInputField from 'components/common/formikForm/fileInputField';
import FormField from 'components/common/formikForm/formField';
import SubmitButton from 'components/common/formikForm/SubmitButton';
import TitleTooltip from 'components/v2/common/titleTooltip';

import api from 'api';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { ClientCompanyDocumentTemplate, DocumentTemplateFormInterface } from 'types';
import {
    organizationDocumentTemplateFormInitialValues,
    organizationDocumentTemplateFormValidationSchema,
} from 'utils/validations/organization';

interface Props {
    title?: string;
    initialData?: ClientCompanyDocumentTemplate | null;
    onClose: () => void;
    onSuccess?: () => void | Promise<void>;
}

const mapInitialValues = (
    initialData?: ClientCompanyDocumentTemplate | null
): DocumentTemplateFormInterface => ({
    ...organizationDocumentTemplateFormInitialValues,
    name_en: initialData?.name_en || '',
    name_fr: initialData?.name_fr || '',
    description_en: initialData?.description_en || '',
    description_fr: initialData?.description_fr || '',
    is_form: Boolean(initialData?.is_form),
    fields_json: initialData?.fields_json
        ? typeof initialData.fields_json === 'string'
            ? initialData.fields_json
            : JSON.stringify(initialData.fields_json, null, 2)
        : '',
    document_type: initialData?.document_type || 'ORGANIZATION',
    template: null,
    sample: null,
});

const DocumentTemplateForm = ({ title, initialData, onClose, onSuccess }: Props) => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [submitting, setSubmitting] = useState(false);
    const isEditMode = Boolean(initialData?.id);

    const handleSubmit = async (values: DocumentTemplateFormInterface) => {
        try {
            setSubmitting(true);

            const payload = new FormData();
            payload.append('name_en', values.name_en.trim());
            payload.append('name_fr', values.name_fr.trim());
            payload.append('description_en', values.description_en.trim());
            payload.append('description_fr', values.description_fr.trim());
            payload.append('is_form', String(values.is_form));
            payload.append('document_type', String(values.document_type));

            if (values.fields_json.trim()) {
                payload.append('fields_json', values.fields_json.trim());
            }

            if (values.template) {
                payload.append('template', values.template);
            }

            if (values.sample) {
                payload.append('sample', values.sample);
            }

            if (isEditMode && initialData?.id) {
                await api.documentTemplates.updateDocumentTemplate(initialData.id, payload);
            } else {
                await api.documentTemplates.createDocumentTemplate(payload);
            }

            dispatch(setAlert({ message: 'saveChanges', type: 'success' }));
            await onSuccess?.();
            onClose();
        } catch {
            dispatch(setAlert({ message: 'somethingWentWrong', type: 'error' }));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            fullScreen={fullScreen}
            scroll="paper"
            slotProps={{
                paper: {
                    sx: {
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        ...(fullScreen ? { height: '100%' } : { height: 'min(90vh, 820px)' }),
                    },
                },
            }}
        >
            <Formik
                enableReinitialize
                initialValues={mapInitialValues(initialData)}
                validationSchema={organizationDocumentTemplateFormValidationSchema}
                onSubmit={handleSubmit}
            >
                <Box
                    component={Form}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    <DialogTitle>
                        <TitleTooltip
                            title={translate(
                                title ||
                                    (isEditMode ? 'editDocumentTemplate' : 'addDocumentTemplate')
                            )}
                        />
                    </DialogTitle>
                    <DialogContent dividers sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                        <Stack spacing={1}>
                            <FormField
                                size="small"
                                name="name_en"
                                label="Name (English)"
                                disabled={submitting}
                            />
                            <FormField
                                size="small"
                                name="name_fr"
                                label="Name (French)"
                                disabled={submitting}
                            />
                            <FormField
                                type="template"
                                size="small"
                                name="description_en"
                                label="Description (English)"
                                multiline
                                minRows={3}
                                disabled={submitting}
                            />
                            <FormField
                                type="template"
                                size="small"
                                name="description_fr"
                                label="Description (French)"
                                multiline
                                minRows={3}
                                disabled={submitting}
                            />
                            <FileInputField
                                required
                                name="template"
                                label="Template file"
                                accept=".pdf,.doc,.docx"
                                existingUri={initialData?.template_uri}
                                disabled={submitting}
                            />
                            <FileInputField
                                name="sample"
                                label="Sample file"
                                accept=".pdf,.doc,.docx"
                                existingUri={initialData?.sample_uri}
                                disabled={submitting}
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ flexShrink: 0 }}>
                        <Button onClick={onClose} color="inherit" disabled={submitting}>
                            {translate('cancel')}
                        </Button>
                        <SubmitButton loading={submitting}>save&Proceed</SubmitButton>
                    </DialogActions>
                </Box>
            </Formik>
        </Dialog>
    );
};

export default DocumentTemplateForm;
